// Route: /accounting/chart-of-accounts/:accountId/history
// Opens when the user clicks "View history" on a Chart of accounts row,
// OR when the user clicks a bank account picker option from inside this page.

import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBankAccountHistory } from "../../../../hooks/useBankAccountHistory";
import { useAllAccountsList } from "../../../../hooks/useAllAccountsList";
import BankHistoryDashboard from "./components/BankHistoryDashboard";
import { AlertCircle, FileText } from "lucide-react";

export default function BankAccountHistoryPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useBankAccountHistory(accountId);
  const { data: allAccounts } = useAllAccountsList();

  if (loading) {
    return (
      <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
        <div className="py-16 flex flex-col items-center gap-2.5">
          <div className="w-1/2 h-3 bg-lineSoft rounded animate-pulse" />
          <div className="w-2/3 h-3 bg-lineSoft rounded animate-pulse" />
          <div className="w-full h-3 bg-lineSoft rounded animate-pulse" />
          <div className="w-full h-3 bg-lineSoft rounded animate-pulse" />
          <div className="w-3/4 h-3 bg-lineSoft rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
        <div className="py-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 text-[#B33E1A] mb-3">
            <AlertCircle size={48} strokeWidth={1.5} />
          </div>
          <h4 className="text-[15px] font-bold text-ink m-0 mb-1.5">Couldn't load the account history</h4>
          <p className="text-[12.5px] text-ink3 m-0 mb-4 max-w-[280px] mx-auto">Something went wrong on our side. Try again in a moment.</p>
          <button type="button" onClick={refetch} className="bg-teal-dark text-white text-[12px] font-bold px-4 py-2 rounded-md border-none cursor-pointer hover:brightness-110">
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
        <div className="py-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 text-ink4 mb-3">
            <FileText size={48} strokeWidth={1.5} />
          </div>
          <h4 className="text-[15px] font-bold text-ink m-0 mb-1.5">Account not found</h4>
          <p className="text-[12.5px] text-ink3 m-0 mb-4 max-w-[280px] mx-auto">That account doesn't exist, or you don't have access to it.</p>
          <button type="button" onClick={() => navigate("/accounting/chart-of-accounts")} className="bg-teal-dark text-white text-[12px] font-bold px-4 py-2 rounded-md border-none cursor-pointer hover:brightness-110">
            Back to chart of accounts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full m-0 -mt-7 px-4 pt-5 pb-24 bg-white md:px-8">
      <BankHistoryDashboard data={data} allAccounts={allAccounts} />
    </div>
  );
}